import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle, X } from 'lucide-react';

const ConfirmContext = createContext(null);

const VARIANTS = {
  warning:  { icon: AlertTriangle, color: '#b45309', bg: '#fffbeb', border: '#f59e0b', button: '#003279' },
  danger:   { icon: Trash2,        color: '#b91c1c', bg: '#fef2f2', border: '#ef4444', button: '#b91c1c' },
  info:     { icon: Info,          color: '#1d4ed8', bg: '#eff6ff', border: '#5BBFEF', button: '#003279' },
  question: { icon: HelpCircle,    color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd', button: '#003279' },
};

export function ConfirmProvider({ children }) {
  const [modal, setModal] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(({
    title = 'Are you sure?',
    message = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'warning', // warning | danger | info | question
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ title, message, confirmText, cancelText, variant });
    });
  }, []);

  /** One-button notification (replaces alert()) */
  const notify = useCallback(({
    title = 'Notice',
    message = '',
    okText = 'OK',
    variant = 'info',
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ title, message, confirmText: okText, cancelText: null, variant, notifyOnly: true });
    });
  }, []);

  /**
   * Multi-choice dialog. Resolves the chosen choice's `key`, or null on
   * cancel/close. A choice with `keepOpen: true` fires its `onSelect`
   * handler without closing the dialog (e.g. "Download PDF" alongside
   * real delivery choices).
   * choices: [{ key, label, description?, keepOpen?, onSelect? }]
   */
  const choose = useCallback(({
    title = 'Choose an option',
    message = '',
    choices = [],
    cancelText = 'Cancel',
    variant = 'question',
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ title, message, choices, cancelText, variant, chooseMode: true });
    });
  }, []);

  const handleConfirm = () => {
    resolveRef.current?.(true);
    setModal(null);
  };

  const handleCancel = () => {
    resolveRef.current?.(modal?.chooseMode ? null : false);
    setModal(null);
  };

  const handleChoice = (choice) => {
    if (choice?.keepOpen) {
      try {
        choice.onSelect?.();
      } catch (err) {
        console.error('[ConfirmModal] choice handler failed:', err);
      }
      return;
    }
    resolveRef.current?.(choice?.key ?? null);
    setModal(null);
  };

  const v = modal ? (VARIANTS[modal.variant] || VARIANTS.warning) : VARIANTS.warning;
  const Icon = v.icon;

  return (
    <ConfirmContext.Provider value={{ confirm, notify, choose }}>
      {children}
      {modal && (
        <div className="confirm-overlay" onClick={handleCancel}>
          <div
            className="confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            style={{
              '--confirm-accent': v.color,
              '--confirm-accent-soft': v.bg,
              '--confirm-accent-border': v.border,
              '--confirm-button': v.button,
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Icon, title, message. The branded strip that used to sit
                above this — a shield, "Paraclete", "Operations notice" and
                a tone chip reading "Critical" or "Review" — announced the
                app on every confirmation, and the eyebrow under it
                repeated in one word what the title says in a sentence.
                A dialog that states its question plainly is more
                convincing than one that introduces itself first. */}
            <div className="confirm-header">
              <div className="confirm-icon-wrap">
                <Icon size={22} />
              </div>
              <div className="confirm-heading">
                <h3 id="confirm-title" className="confirm-title">{modal.title}</h3>
              </div>
            </div>
            <div className="confirm-content">
              {modal.message && <p className="confirm-message">{modal.message}</p>}
              {modal.chooseMode && (
                <div className="confirm-choices">
                  {(modal.choices || []).map((choice) => (
                    <button
                      key={choice.key}
                      type="button"
                      className={`confirm-choice-btn${choice.keepOpen ? ' confirm-choice-btn--secondary' : ''}`}
                      onClick={() => handleChoice(choice)}
                    >
                      <span className="confirm-choice-label">{choice.label}</span>
                      {choice.description && (
                        <span className="confirm-choice-desc">{choice.description}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="confirm-actions">
              {modal.cancelText && (
                <button className="btn btn-ghost confirm-cancel-btn" onClick={handleCancel}>
                  {modal.cancelText}
                </button>
              )}
              {!modal.chooseMode && (
                <button className="btn confirm-ok-btn" onClick={handleConfirm}>
                  {modal.confirmText}
                </button>
              )}
            </div>
            <button className="confirm-close-btn" onClick={handleCancel}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  // Backward compat: ctx is callable as confirm(), or destructure { confirm, notify, choose }
  return useMemo(() => Object.assign(
    (...args) => ctx.confirm(...args),
    { confirm: ctx.confirm, notify: ctx.notify, choose: ctx.choose },
  ), [ctx]);
}
